import { supabase } from './supabase';
import type { User, Session } from '@supabase/supabase-js';

export class AuthService {
  // Sign up new user
  async signUp(email: string, password: string): Promise<{ user: User | null; error: Error | null }> {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
    });

    return {
      user: data.user,
      error: error ? new Error(error.message) : null,
    };
  }

  // Sign in existing user
  async signIn(email: string, password: string): Promise<{ user: User | null; error: Error | null }> {
    console.log('🔑 AuthService.signIn called', { email });

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      console.log('📥 Supabase response:', {
        hasUser: !!data.user,
        userEmail: data.user?.email,
        hasSession: !!data.session,
        error: error?.message
      });

      return {
        user: data.user,
        error: error ? new Error(error.message) : null,
      };
    } catch (err) {
      console.error('💥 Exception in signIn:', err);
      return {
        user: null,
        error: err instanceof Error ? err : new Error('Unknown error'),
      };
    }
  }

  // Sign out current user
  async signOut(): Promise<{ error: Error | null }> {
    const { error } = await supabase.auth.signOut();

    return {
      error: error ? new Error(error.message) : null,
    };
  }

  // Get current session
  async getSession(): Promise<Session | null> {
    const { data: { session } } = await supabase.auth.getSession();
    return session;
  }

  // Get current user
  async getUser(): Promise<User | null> {
    const { data: { user } } = await supabase.auth.getUser();
    return user;
  }

  // Listen to auth state changes
  onAuthStateChange(callback: (user: User | null, session: Session | null) => void) {
    return supabase.auth.onAuthStateChange((_event, session) => {
      callback(session?.user ?? null, session);
    });
  }
}

// Export singleton instance
export const authService = new AuthService();
